import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import BlogComments from '../components/BlogComments'
import { renderMarkdown } from '../lib/markdown'
import { blogApi, type BlogPostDetail } from '../services/api'
import '../styles/blog.css'

function formatDate(iso: string) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function buildArticleJsonLd(post: BlogPostDetail) {
  const url = `https://opendeploy.dev/blog/${post.slug}`
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#post`,
    headline: post.title,
    description: post.description,
    url,
    mainEntityOfPage: url,
    datePublished: post.published_at,
    inLanguage: 'en',
    keywords: post.tags.join(', '),
    articleSection: post.category,
    author: { '@id': 'https://opendeploy.dev/#organization' },
    publisher: { '@id': 'https://opendeploy.dev/#organization' },
    image: post.cover_image_url || 'https://opendeploy.dev/og-image.png',
  }
}

function buildBreadcrumbJsonLd(post: BlogPostDetail) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Blog', item: 'https://opendeploy.dev/blog' },
      {
        '@type': 'ListItem',
        position: 2,
        name: post.title,
        item: `https://opendeploy.dev/blog/${post.slug}`,
      },
    ],
  }
}

export default function BlogPostPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [post, setPost] = useState<BlogPostDetail | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'notfound'>('loading')

  useEffect(() => {
    let active = true
    setStatus('loading')
    blogApi
      .getPost(slug)
      .then((res) => {
        if (!active) return
        setPost(res.data)
        setStatus('ready')
      })
      .catch(() => {
        if (active) setStatus('notfound')
      })
    return () => {
      active = false
    }
  }, [slug])

  useEffect(() => {
    if (status === 'notfound') {
      const t = setTimeout(() => navigate('/blog', { replace: true }), 2200)
      return () => clearTimeout(t)
    }
  }, [status, navigate])

  const rendered = useMemo(
    () => (post ? renderMarkdown(post.body_markdown) : { html: '', toc: [] }),
    [post],
  )

  if (status === 'loading') {
    return (
      <div className="od-blog-page od-blog-light">
        <SiteNav />
        <div className="od-blog-post-frame">
          <p className="od-blog-state">Loading…</p>
        </div>
        <Footer />
      </div>
    )
  }

  if (status === 'notfound' || !post) {
    return (
      <div className="od-blog-page od-blog-light">
        <SiteNav />
        <div className="od-blog-post-frame">
          <p className="od-blog-state">
            Post not found — taking you back to the <Link to="/blog">blog</Link>.
          </p>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <>
      <SEO
        title={`${post.title} — OpenDeploy Blog`}
        description={post.description}
        path={`/blog/${post.slug}`}
        jsonLd={[buildArticleJsonLd(post), buildBreadcrumbJsonLd(post)]}
      />

      <div className="od-blog-page od-blog-light od-blog-post-page">
        <SiteNav />
        <div className="od-blog-post-frame">
          <main className="od-blog-article">
            <nav className="od-blog-breadcrumb" aria-label="Breadcrumb">
              <Link to="/blog">Blog</Link>
              <span aria-hidden="true">/</span>
              <span>{post.category}</span>
            </nav>

            <header className="od-blog-article-hero">
              <p className="od-blog-kicker">{post.category}</p>
              <h1>{post.title}</h1>
              {post.tldr && <p className="od-blog-tldr">{post.tldr}</p>}

              <div className="od-blog-meta">
                {post.published_at && <span>Published {formatDate(post.published_at)}</span>}
                {post.read_time && <span>{post.read_time}</span>}
              </div>

              {post.tags.length > 0 && (
                <div className="od-blog-tag-row">
                  {post.tags.map((tag) => (
                    <span key={tag} className="od-blog-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {post.cover_image_url && (
              <img className="od-blog-cover" src={post.cover_image_url} alt="" />
            )}

            <div
              className="od-blog-body"
              dangerouslySetInnerHTML={{ __html: rendered.html }}
            />

            <BlogComments slug={post.slug} commentsEnabled={post.comments_enabled} />
          </main>

          {rendered.toc.length > 0 && (
            <aside className="od-blog-toc" aria-label="On this page">
              <p>On this page</p>
              {rendered.toc.map((item) => (
                <a key={item.id} href={`#${item.id}`}>
                  {item.label}
                </a>
              ))}
            </aside>
          )}
        </div>
        <Footer />
      </div>
    </>
  )
}
